import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

import { useSearch } from '@/react/portainer/gitops/queries/useSearch';
import { useSource } from '@/react/portainer/gitops/sources/queries/useSource';
import { RefSelector } from '@/react/portainer/gitops/RefField/RefSelector';
import { useGitRefs } from '@/react/portainer/gitops/queries/useGitRefs';
import { SourceId } from '@/react/portainer/gitops/sources/types';

import { FilePicker } from '@@/form-components/FilePicker/FilePicker';
import { FilePickerSkeleton } from '@@/form-components/FilePicker/FilePickerSkeleton';
import { buildFileTree } from '@@/form-components/FilePicker/utils';
import { Widget } from '@@/Widget';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { Alert } from '@@/Alert';

import { artifactsStepValidationSchema } from '../validation';
import { WizardMode, WizardStep, useWizardContext } from '../WizardContext';
import { updateFirstArtifact } from '../updateFirstArtifact';
import { FormValues } from '../types';

import { StepTitle } from './StepTitle';

export const ArtifactsStep: WizardStep = {
  id: 'artifacts',
  label: 'Artifacts & stacks',
  Component,
  validateStep,
};

function Component() {
  const { values, setValues } = useFormikContext<FormValues>();
  const isEditing = useWizardContext().mode === WizardMode.Edit;
  const sourceId = values.sources[0];

  return (
    <>
      <StepTitle
        title="Configure artifacts & stacks"
        subtitle="Browse your source and select files to create the stack"
      />

      {values.artifacts.length > 1 && (
        <Alert color="info" className="mb-4">
          This workflow has {values.artifacts.length} artifacts. Only the first
          one&apos;s source and file can be changed here; targets and
          configuration for every artifact are on the next step.
        </Alert>
      )}

      <FormControl inputId="artifact-name" label="Name" required>
        <Input
          id="artifact-name"
          onChange={(e) =>
            setValues((prev) =>
              updateFirstArtifact(prev, { name: e.target.value })
            )
          }
          placeholder="my-stack"
          value={values.artifacts[0].name}
          data-cy="artifact-name-input"
          readOnly={isEditing}
          disabled={isEditing}
          required
        />
      </FormControl>

      <SourcePicker sourceId={sourceId} />
    </>
  );
}

function SourcePicker({ sourceId }: { sourceId: SourceId }) {
  const [gitRef, setGitRef] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const { values, setValues } = useFormikContext<FormValues>();
  const sourceQuery = useSource(sourceId);
  const refsQuery = useGitRefs({ sourceId });
  const searchQuery = useSearch(
    { sourceId, reference: gitRef },
    { enabled: !refsQuery.isLoading }
  );

  const fileTree = useMemo(
    () => buildFileTree(searchQuery.data || []),
    [searchQuery.data]
  );

  const isLoading =
    sourceQuery.isLoading || refsQuery.isLoading || searchQuery.isLoading;

  return (
    <Widget>
      <Widget.Taskbar className="!mb-0 flex items-center gap-2">
        <CollapseExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        {!sourceQuery.isLoading && <span>{sourceQuery.data?.name}</span>}
        <div className="ml-auto">
          <RefSelector
            inputId="ref-field"
            value={gitRef}
            onChange={(ref) => {
              setGitRef(ref);
              setValues((prev) =>
                updateFirstArtifact(prev, {
                  files: prev.artifacts[0].files.map((file) => ({
                    ...file,
                    ref,
                  })),
                })
              );
            }}
            sourceId={sourceId}
            size="sm"
          />
        </div>
      </Widget.Taskbar>
      {isExpanded && (
        <Widget.Body>
          {isLoading && <FilePickerSkeleton />}
          {!isLoading && (
            <FilePicker
              files={fileTree}
              onChange={(files) => handleFilesChange(files)}
              value={values.artifacts[0].files.map(({ path }) => `/${path}`)}
              disableDirSelect
              disableMultiSelect
            />
          )}
        </Widget.Body>
      )}
    </Widget>
  );

  function handleFilesChange(files: string[]) {
    const artifactFiles = files.map((path) => ({
      sourceId,
      path: path.startsWith('/') ? path.replace('/', '') : path,
      ref: gitRef,
    }));

    setValues((prev) => updateFirstArtifact(prev, { files: artifactFiles }));
  }
}

function validateStep() {
  return artifactsStepValidationSchema();
}
